import React, { useCallback, useEffect, useState } from 'react';
import { ArrowDown, ArrowUp, Monitor, RefreshCw } from 'lucide-react';
import Button from './Button';
import LoadingSpinner from './LoadingSpinner';
import { SessionVideoClip } from '../hooks/useDesktopSessionVideos';
import { api } from '../hooks/useAuth';
import toast from 'react-hot-toast';
import {
  copyDesktopRecorderCommand,
  copySessionId,
  getDesktopRecorderLaunchCommand,
  openDesktopRecorder,
} from '../utils/desktopRecorder';

interface DesktopClipsPanelProps {
  sessionId: string;
  ticketId?: string | null;
  clips: SessionVideoClip[];
  isLoading: boolean;
  sessionMissing?: boolean;
  onRefresh: () => void;
  onResetSession?: () => void;
}

const DesktopClipsPanel: React.FC<DesktopClipsPanelProps> = ({
  sessionId,
  ticketId,
  clips,
  isLoading,
  sessionMissing = false,
  onRefresh,
  onResetSession,
}) => {
  const [orderedIds, setOrderedIds] = useState<string[]>([]);
  const [savingOrder, setSavingOrder] = useState(false);

  useEffect(() => {
    setOrderedIds(clips.map((c) => c.id));
  }, [clips]);

  const orderedClips = orderedIds
    .map((id) => clips.find((c) => c.id === id))
    .filter((c): c is SessionVideoClip => !!c);

  const move = (index: number, direction: -1 | 1) => {
    const next = [...orderedIds];
    const target = index + direction;
    if (target < 0 || target >= next.length) return;
    [next[index], next[target]] = [next[target], next[index]];
    setOrderedIds(next);
  };

  const saveOrder = useCallback(async () => {
    if (orderedIds.length < 2) return;
    setSavingOrder(true);
    try {
      await api.put(`/videos/session/${sessionId}/order`, { videoIds: orderedIds });
      toast.success('Clip order saved');
      onRefresh();
    } catch {
      toast.error('Failed to save clip order');
    } finally {
      setSavingOrder(false);
    }
  }, [orderedIds, onRefresh, sessionId]);

  const orderDirty =
    orderedIds.length > 0 &&
    clips.length > 0 &&
    orderedIds.some((id, i) => clips[i]?.id !== id);

  return (
    <div className="w-full max-w-lg mx-auto space-y-4 text-left">
      {sessionMissing && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-900 mb-3">
          <p className="font-semibold">Session not found on server</p>
          <p className="mt-1 opacity-90">
            The session ID on this page is invalid or was deleted. Create a new session, then open the desktop
            recorder again.
          </p>
          {onResetSession && (
            <Button variant="danger" size="sm" className="mt-3" onClick={onResetSession}>
              Reset recording session
            </Button>
          )}
        </div>
      )}

      <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-950">
        <div className="flex items-start gap-2">
          <Monitor className="h-5 w-5 shrink-0 mt-0.5" />
          <div>
            <p className="font-semibold">Waiting for clips from desktop app</p>
            <p className="mt-1 opacity-90">
              Open the recorder, sign in, and record segments. While recording, the app stays as a compact bar with{' '}
              <strong>Stop &amp; upload</strong> (or <strong>Ctrl+Shift+S</strong>). Reorder clips here, then use{' '}
              <strong>End Session &amp; Combine Videos</strong> (green card below the clip list) when finished.
            </p>
            <p className="text-xs font-mono break-all mt-2 bg-white/50 rounded px-2 py-1">
              Session: {sessionId}
            </p>
          </div>
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        <Button variant="primary" size="md" onClick={() => void openDesktopRecorder(sessionId, ticketId)}>
          Open desktop recorder
        </Button>
        <Button variant="outline" size="md" onClick={() => copyDesktopRecorderCommand(sessionId)}>
          Copy launch command
        </Button>
        <Button variant="ghost" size="md" onClick={() => copySessionId(sessionId)}>
          Copy session ID
        </Button>
      </div>

      <p className="text-xs text-gray-500 font-mono break-all">{getDesktopRecorderLaunchCommand(sessionId)}</p>

      <div className="rounded-lg border border-gray-200 bg-white p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold text-gray-900">
            Uploaded clips ({clips.length})
          </h3>
          <button
            type="button"
            className="text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1"
            onClick={onRefresh}
          >
            <RefreshCw className="h-4 w-4" />
            Refresh
          </button>
        </div>

        {isLoading && clips.length === 0 ? (
          <div className="flex justify-center py-6">
            <LoadingSpinner />
          </div>
        ) : clips.length === 0 ? (
          <p className="text-sm text-gray-500 py-4 text-center">No clips uploaded yet.</p>
        ) : (
          <>
            <ul className="space-y-2">
              {orderedClips.map((clip, index) => (
                <li
                  key={clip.id}
                  className="flex items-center gap-2 rounded border border-gray-100 bg-gray-50 px-3 py-2 text-sm"
                >
                  <span className="font-mono text-gray-400 w-6">{index + 1}.</span>
                  <span className="flex-1 truncate" title={clip.originalFilename}>
                    {clip.originalFilename || clip.filename}
                  </span>
                  {clip.durationSeconds != null && (
                    <span className="text-gray-500 text-xs">{clip.durationSeconds}s</span>
                  )}
                  <div className="flex gap-1">
                    <button
                      type="button"
                      className="p-1 rounded hover:bg-gray-200 disabled:opacity-30"
                      disabled={index === 0}
                      onClick={() => move(index, -1)}
                      aria-label="Move up"
                    >
                      <ArrowUp className="h-4 w-4" />
                    </button>
                    <button
                      type="button"
                      className="p-1 rounded hover:bg-gray-200 disabled:opacity-30"
                      disabled={index === orderedClips.length - 1}
                      onClick={() => move(index, 1)}
                      aria-label="Move down"
                    >
                      <ArrowDown className="h-4 w-4" />
                    </button>
                  </div>
                </li>
              ))}
            </ul>
            {orderDirty && (
              <Button
                variant="outline"
                size="sm"
                className="mt-3"
                onClick={() => void saveOrder()}
                isLoading={savingOrder}
              >
                Save clip order
              </Button>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default DesktopClipsPanel;
